/**
 * Subscription frequency cards (Loop) — works on PDP and quickshop AJAX inject.
 * Uses document-level delegation so handlers exist even when the block's
 * {% javascript %} never loads (e.g. collection pages).
 * Keeps sticky bar dropdown + CTA in two-way sync with the main cards.
 */
(function () {
  let syncingSelection = false;

  function getStickySelect() {
    return document.querySelector('[data-sticky-sub-select]');
  }

  function isProductPageForm(form) {
    const format = form?.closest?.('product-form')?.dataset?.format;
    if (format === 'quickview' || format === 'product-card') return false;
    return format === 'product-page' || window.location.pathname.includes('/products/');
  }

  function updateVariantUrl(variantId, form) {
    if (!variantId || !isProductPageForm(form)) return;

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variantId));
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    } catch (error) {
      const path = window.location.pathname;
      window.history.replaceState({}, '', `${path}?variant=${variantId}`);
    }
  }

  function syncVariantPicker(variantId, form) {
    if (!variantId || !isProductPageForm(form)) return;

    const variantSelects = form.closest('product-form')?.querySelector('variant-selects');
    if (!variantSelects) return;

    const jsonEl = variantSelects.querySelector('[data-variantJSON]');
    if (!jsonEl) return;

    let variants;
    try {
      variants = JSON.parse(jsonEl.textContent);
    } catch (error) {
      return;
    }

    const variant = variants.find((item) => String(item.id) === String(variantId));
    if (!variant?.options) return;

    variant.options.forEach((optionValue, index) => {
      const optionPicker = variantSelects.querySelector(
        `[data-product-option][data-optionindex="${index + 1}"]`
      );
      if (!optionPicker) return;

      const radio = Array.from(optionPicker.querySelectorAll('input[type="radio"]')).find(
        (input) => input.value === optionValue
      );
      if (radio) radio.checked = true;

      const select = optionPicker.querySelector('select.variant_selector');
      if (select && select.value !== optionValue) {
        Array.from(select.options).forEach((opt) => {
          opt.selected = opt.value === optionValue;
        });
        select.value = optionValue;
      }

      const label = optionPicker.querySelector('[data-selected-value]');
      if (label) label.textContent = optionValue;
    });
  }

  function getStickyForm() {
    return document.querySelector('product-form[data-sticky-form] form');
  }

  function getStickyAtc() {
    return document.querySelector('[data-sticky-atc]');
  }

  function closeStickySelect(root) {
    const select = root || getStickySelect();
    if (!select) return;
    const trigger = select.querySelector('[data-sticky-sub-trigger]');
    const menu = select.querySelector('[data-sticky-sub-menu]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (menu) menu.hidden = true;
    select.classList.remove('is-open');
  }

  function openStickySelect(select) {
    if (!select) return;
    const trigger = select.querySelector('[data-sticky-sub-trigger]');
    const menu = select.querySelector('[data-sticky-sub-menu]');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    if (menu) menu.hidden = false;
    select.classList.add('is-open');
  }

  function syncStickyForm(variantId, sellingPlanId) {
    const stickyForm = getStickyForm();
    if (!stickyForm) return;

    const stickyVariant = stickyForm.querySelector('input[name="id"]');
    let stickyPlan = stickyForm.querySelector('input[name="selling_plan"]');
    if (!stickyPlan) {
      stickyPlan = document.createElement('input');
      stickyPlan.type = 'hidden';
      stickyPlan.name = 'selling_plan';
      stickyPlan.className = 'selling-plan-input';
      stickyForm.appendChild(stickyPlan);
    }
    if (stickyVariant && variantId) stickyVariant.value = variantId;
    stickyPlan.value = sellingPlanId || '';
  }

  function syncAtcLabelsFromMain() {
    const mainAtc = document.querySelector('[data-subscription-buy-buttons] [data-subscription-atc]');
    const stickyAtc = getStickyAtc();
    if (!mainAtc || !stickyAtc) return;

    if (mainAtc.dataset.labelSubscribe) {
      stickyAtc.dataset.labelSubscribe = mainAtc.dataset.labelSubscribe;
    }
    if (mainAtc.dataset.labelOnetime) {
      stickyAtc.dataset.labelOnetime = mainAtc.dataset.labelOnetime;
    }
  }

  function setLabelText(el, text) {
    if (!el) return;
    el.textContent = text;
    el.setAttribute('data-text', text);
  }

  function updateAtcLabel(container, frequency, savePct) {
    const atcBtn = container?.querySelector?.('[data-subscription-atc]');
    const atcLabel = atcBtn?.querySelector('[data-atc-label]');
    if (atcBtn && atcLabel) {
      const subscribeLabel = atcBtn.dataset.labelSubscribe || 'Subscribe & Save';
      const onetimeLabel = atcBtn.dataset.labelOnetime || 'Add to cart';

      if (frequency === 'one-time') {
        setLabelText(atcLabel, onetimeLabel);
      } else {
        setLabelText(atcLabel, savePct ? `${subscribeLabel} ${savePct}%` : subscribeLabel);
      }
    }

    updateStickyAtcLabel(frequency, savePct);
  }

  function getStickyOptionPrice(frequency) {
    const option = document.querySelector(
      frequency
        ? `[data-sticky-sub-option][data-frequency="${frequency}"]`
        : '[data-sticky-sub-option].is-selected'
    );
    return (option?.dataset?.price || '').toString().trim();
  }

  function updateStickyAtcLabel(frequency, savePct) {
    const stickyAtc = getStickyAtc();
    const stickyLabel = stickyAtc?.querySelector('[data-sticky-atc-label]');
    if (!stickyAtc || !stickyLabel) return;

    syncAtcLabelsFromMain();

    const subscribeLabel = stickyAtc.dataset.labelSubscribe || 'Subscribe & Save';
    const subscribeMobileLabel = stickyAtc.dataset.labelSubscribeMobile || 'subscribe';
    const onetimeLabel = stickyAtc.dataset.labelOnetime || 'Add to cart';
    const price = getStickyOptionPrice(frequency);
    const isOnetime = frequency === 'one-time';
    const desktopText = isOnetime
      ? onetimeLabel
      : savePct
        ? `${subscribeLabel} ${savePct}%`
        : subscribeLabel;
    const mobilePrefix = isOnetime ? onetimeLabel : subscribeMobileLabel;
    const mobileText = price ? `${mobilePrefix} - ${price}` : mobilePrefix;

    const desktopEl = stickyLabel.querySelector('[data-sticky-atc-label-desktop]');
    const mobileEl = stickyLabel.querySelector('[data-sticky-atc-label-mobile]');

    if (desktopEl && mobileEl) {
      desktopEl.textContent = desktopText;
      mobileEl.textContent = mobileText;
      stickyLabel.setAttribute('data-text', desktopText);
      return;
    }

    setLabelText(stickyLabel, desktopText);
  }

  function syncStickySelectUI(frequency) {
    const select = getStickySelect();
    if (!select || !frequency) return;

    const options = select.querySelectorAll('[data-sticky-sub-option]');
    const labelEl = select.querySelector('[data-sticky-sub-label]');
    let matched = null;

    options.forEach((option) => {
      const isMatch = option.dataset.frequency === frequency;
      option.classList.toggle('is-selected', isMatch);
      option.setAttribute('aria-selected', isMatch ? 'true' : 'false');
      if (isMatch) matched = option;
    });

    if (matched && labelEl) {
      // labelEl.textContent = matched.dataset.label || matched.textContent.trim();
      labelEl.innerHTML = matched.dataset.label || matched.textContent.trim();
    }
  }

  function selectOption(wrap, { fromSticky, fromVariantPicker } = {}) {
    if (!wrap || syncingSelection) return;

    const container = wrap.closest('[data-subscription-buy-buttons]');
    if (!container) return;

    const wraps = container.querySelectorAll('.subscription-option');
    const form = container.querySelector('form[data-type="add-to-cart-form"]');
    const variantInput = form?.querySelector('input.product-variant-id, input[name="id"]');
    const sellingPlanInput = form?.querySelector('input.selling-plan-input, input[name="selling_plan"]');

    wraps.forEach((el) => {
      el.classList.remove('is-active');
      el.setAttribute('aria-checked', 'false');
    });

    wrap.classList.add('is-active');
    wrap.setAttribute('aria-checked', 'true');

    const frequency = wrap.dataset.frequency || '';
    const sellingPlanId = (wrap.dataset.id || '').toString().trim();
    const variantId = wrap.dataset.variantId;
    const savePct = (wrap.dataset.savePct || '').toString().trim();

    if (variantInput && variantId) variantInput.value = variantId;
    if (sellingPlanInput) sellingPlanInput.value = frequency === 'one-time' ? '' : sellingPlanId;

    updateAtcLabel(container, frequency, savePct);
    syncStickyForm(variantId, frequency === 'one-time' ? '' : sellingPlanId);
    syncStickySelectUI(frequency);
    updateVariantUrl(variantId, form);

    if (!fromVariantPicker) {
      syncingSelection = true;
      try {
        syncVariantPicker(variantId, form);
      } finally {
        syncingSelection = false;
      }
    }

    if (fromSticky) closeStickySelect();
  }

  function selectStickyOption(option) {
    if (!option) return;

    const frequency = option.dataset.frequency || '';
    const mainOption = document.querySelector(
      `[data-subscription-buy-buttons] .subscription-option[data-frequency="${frequency}"]`
    );

    if (mainOption) {
      selectOption(mainOption, { fromSticky: true });
      return;
    }

    // Fallback if main cards are missing
    const sellingPlanId = (option.dataset.id || '').toString().trim();
    const variantId = option.dataset.variantId;
    const savePct = (option.dataset.savePct || '').toString().trim();

    syncStickyForm(variantId, frequency === 'one-time' ? '' : sellingPlanId);
    syncStickySelectUI(frequency);
    updateStickyAtcLabel(frequency, savePct);
    updateVariantUrl(variantId, getStickyForm());
    closeStickySelect();
  }

  function syncDefaults(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-subscription-buy-buttons]').forEach((container) => {
      const active =
        container.querySelector('.subscription-option.is-active') ||
        container.querySelector('.subscription-option');
      if (active) selectOption(active);
    });
    syncAtcLabelsFromMain();
  }

  document.addEventListener('click', (evt) => {
    const stickyOption = evt.target.closest('[data-sticky-sub-option]');
    if (stickyOption) {
      evt.preventDefault();
      selectStickyOption(stickyOption);
      return;
    }

    const stickyTrigger = evt.target.closest('[data-sticky-sub-trigger]');
    if (stickyTrigger) {
      evt.preventDefault();
      const select = stickyTrigger.closest('[data-sticky-sub-select]');
      if (!select) return;
      const isOpen = select.classList.contains('is-open');
      closeStickySelect();
      if (!isOpen) openStickySelect(select);
      return;
    }

    if (!evt.target.closest('[data-sticky-sub-select]')) {
      closeStickySelect();
    }

    const wrap = evt.target.closest('.subscription-option');
    if (!wrap || !wrap.closest('[data-subscription-buy-buttons]')) return;
    if (evt.target.closest('a, button')) return;
    selectOption(wrap);
  });

  document.addEventListener('keydown', (evt) => {
    if (evt.key === 'Escape') {
      closeStickySelect();
      return;
    }

    if (evt.key !== 'Enter' && evt.key !== ' ') return;

    const stickyOption = evt.target.closest('[data-sticky-sub-option]');
    if (stickyOption) {
      evt.preventDefault();
      selectStickyOption(stickyOption);
      return;
    }

    const stickyTrigger = evt.target.closest('[data-sticky-sub-trigger]');
    if (stickyTrigger) {
      evt.preventDefault();
      stickyTrigger.click();
      return;
    }

    const wrap = evt.target.closest('.subscription-option');
    if (!wrap || !wrap.closest('[data-subscription-buy-buttons]')) return;
    evt.preventDefault();
    selectOption(wrap);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => syncDefaults(document));
  } else {
    syncDefaults(document);
  }

  document.addEventListener('shopify:section:load', (evt) => {
    syncDefaults(evt.target || document);
  });

  document.addEventListener('quickshop:loaded', (evt) => {
    syncDefaults(evt.detail?.drawer || document);
  });

  if (window.eventBus && window.THEME_EVENTS?.variantChange) {
    eventBus.subscribe(THEME_EVENTS.variantChange, (eventData) => {
      const variant = eventData?.data?.variant;
      if (!variant?.id || syncingSelection) return;

      const option = document.querySelector(
        `[data-subscription-buy-buttons] .subscription-option[data-variant-id="${variant.id}"]`
      );
      if (!option || option.classList.contains('is-active')) return;

      selectOption(option, { fromVariantPicker: true });
    });
  }

  window.SubscriptionBuyButtons = {
    selectOption,
    selectStickyOption,
    syncDefaults,
    syncStickySelectUI,
    updateStickyAtcLabel,
    closeStickySelect,
  };
})();
